import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ArrowLeft, Plus, Trash2, LogOut, Shield, Building2, Settings } from 'lucide-react';
import { toast } from 'sonner';
import DepartmentList from '@/components/DepartmentList';
import KRAKPIManager from '@/components/KRAKPIManager';
import ReviewManager from '@/components/ReviewManager';
import AnalyticsDashboard from '@/components/AnalyticsDashboard';
import ProfileSettings from '@/components/ProfileSettings';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

export default function CEODashboard() {
  const navigate = useNavigate();
  const { 
    getActiveTenantId, 
    tenantName, 
    isSuperAdmin, 
    isSchoolAdmin,
    selectedTenantId,
    selectTenant,
    clearTenantSelection,
    logout 
  } = useAuth();
  
  const [departments, setDepartments] = useState([]);
  const [selectedDept, setSelectedDept] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [newDeptName, setNewDeptName] = useState('');
  const [newDeptHead, setNewDeptHead] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [tenants, setTenants] = useState([]);
  const [currentUsername, setCurrentUsername] = useState('');

  useEffect(() => {
    // Redirect if not authenticated
    if (!isSuperAdmin && !isSchoolAdmin) {
      navigate('/');
      return;
    }
    
    // Fetch current user info
    fetchCurrentUser();
    
    // Fetch tenants if Super Admin
    if (isSuperAdmin) {
      fetchTenants();
    }
    
    fetchDepartments();
  }, [isSuperAdmin, isSchoolAdmin, navigate, selectedTenantId]);

  const fetchCurrentUser = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCurrentUsername(response.data.username);
    } catch (error) {
      console.error('Error fetching user info:', error);
    }
  };

  const fetchTenants = async () => {
    try {
      const response = await axios.get(`${API}/super-admin/tenants`);
      setTenants(response.data.filter(t => t.status === 'active'));
    } catch (error) {
      console.error('Error fetching tenants:', error);
    }
  };

  const fetchDepartments = async () => {
    try {
      const tenantId = getActiveTenantId();
      const url = tenantId 
        ? `${API}/departments?tenant_id=${tenantId}`
        : `${API}/departments`;
      
      const response = await axios.get(url);
      setDepartments(response.data);
    } catch (error) {
      console.error('Error fetching departments:', error);
      toast.error('Failed to load departments');
    } finally {
      setLoading(false);
    }
  };

  const handleAddDepartment = async () => {
    if (!newDeptName.trim()) {
      toast.error('Department name is required');
      return;
    }

    const tenantId = getActiveTenantId();
    if (!tenantId) {
      toast.error('Please select a school to add departments');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(
        `${API}/departments?name=${encodeURIComponent(newDeptName)}&head_name=${encodeURIComponent(newDeptHead)}&tenant_id=${tenantId}`
      );
      toast.success('Department added successfully');
      setNewDeptName('');
      setNewDeptHead('');
      setShowAddDialog(false);
      fetchDepartments();
    } catch (error) {
      console.error('Error adding department:', error);
      toast.error(error.response?.data?.detail || 'Failed to add department');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteDepartment = async (deptId, deptName, isPredefined) => {
    // School admins can delete their own predefined departments
    // Only Super Admin managing default template cannot delete predefined ones
    const confirmMessage = isPredefined 
      ? `"${deptName}" is a predefined department. Are you sure you want to remove it from your school? This will also delete all associated KRAs, KPIs, and reviews.`
      : `Are you sure you want to delete "${deptName}" department? This will also delete all associated KRAs, KPIs, and reviews.`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      const tenantId = getActiveTenantId();
      const url = tenantId 
        ? `${API}/departments/${deptId}?tenant_id=${tenantId}`
        : `${API}/departments/${deptId}`;
      
      await axios.delete(url);
      toast.success('Department deleted successfully');
      fetchDepartments();
    } catch (error) {
      console.error('Error deleting department:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete department');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    toast.success('Logged out successfully');
  };

  const handleBackToSuperAdmin = () => {
    clearTenantSelection();
    navigate('/super-admin');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* Top Navigation Bar */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
                {isSuperAdmin ? <Shield className="w-5 h-5 text-white" /> : <Building2 className="w-5 h-5 text-white" />}
              </div>
              <div>
                <h1 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  {isSuperAdmin ? 'Super Admin View' : 'School Admin Dashboard'}
                </h1>
                <p className="text-sm text-slate-600">{tenantName || 'Loading...'}</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-3">
              {isSuperAdmin && selectedTenantId && (
                <Button
                  onClick={handleBackToSuperAdmin}
                  variant="outline"
                  size="sm"
                  className="flex items-center space-x-2"
                >
                  <ArrowLeft className="w-4 h-4" />
                  <span>Back to Super Admin</span>
                </Button>
              )}
              <Button
                onClick={handleLogout}
                variant="outline"
                size="sm"
                className="flex items-center space-x-2"
              >
                <LogOut className="w-4 h-4" />
                <span>Logout</span>
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-6">
        {/* Tenant Selection for Super Admin */}
        {isSuperAdmin && !selectedTenantId && (
          <Card className="p-6 mb-6 bg-gradient-to-r from-indigo-50 to-purple-50 border-2 border-indigo-200">
            <h2 className="text-lg font-bold text-slate-900 mb-4">Select a School to View</h2>
            <p className="text-sm text-slate-600 mb-4">
              Choose a school to view and manage their departments, KRAs, KPIs, and reviews.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {tenants.map((tenant) => (
                <Card
                  key={tenant.id}
                  className="p-4 hover:border-indigo-500 hover:shadow-lg transition-all cursor-pointer border-2"
                  onClick={() => {
                    selectTenant(tenant.id);
                    fetchDepartments();
                  }}
                >
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-cyan-600 flex items-center justify-center">
                      <Building2 className="w-5 h-5 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-slate-900 text-sm">{tenant.school_name}</h3>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </Card>
        )}

        {/* Show message if Super Admin hasn't selected a tenant */}
        {isSuperAdmin && !selectedTenantId && (
          <Card className="p-12 text-center bg-white">
            <Shield className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-slate-900 mb-2">Select a School</h3>
            <p className="text-slate-600">
              Please select a school from above to view their dashboard, or return to Super Admin panel.
            </p>
          </Card>
        )}

        {/* Show dashboard only if tenant is selected (for both Super Admin and School Admin) */}
        {(isSchoolAdmin || (isSuperAdmin && selectedTenantId)) && (
          <>
            <div className="mb-6 flex items-center justify-between">
              <div>
                <h2 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  Promoter's Dashboard
                </h2>
                <p className="text-slate-600 mt-1">Manage departments, KRAs, KPIs, and conduct reviews</p>
              </div>
              {isSchoolAdmin && (
                <Button
                  onClick={() => navigate('/school-role-selection')}
                  className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-2 rounded-lg shadow-md flex items-center space-x-2"
                >
                  <ArrowLeft className="w-4 h-4" />
                  <span>Review Management System</span>
                </Button>
              )}
            </div>

        <Tabs defaultValue="overview" className="space-y-6">
          <TabsList className="grid w-full grid-cols-5 bg-white p-1 rounded-xl shadow-sm" data-testid="ceo-tabs">
            <TabsTrigger value="overview" className="rounded-lg" data-testid="overview-tab">Overview</TabsTrigger>
            <TabsTrigger value="kra-kpi" className="rounded-lg" data-testid="kra-kpi-tab">Manage KRA/KPI</TabsTrigger>
            <TabsTrigger value="reviews" className="rounded-lg" data-testid="reviews-tab">Conduct Reviews</TabsTrigger>
            <TabsTrigger value="analytics" className="rounded-lg" data-testid="analytics-tab">Analytics</TabsTrigger>
            <TabsTrigger value="profile" className="rounded-lg flex items-center gap-2" data-testid="profile-tab">
              <Settings className="w-4 h-4" />
              Profile
            </TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-6">
            <Card className="p-6 bg-white shadow-lg">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-2xl font-bold text-slate-900">All Departments</h2>
                <Button
                  onClick={() => setShowAddDialog(true)}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="add-department-button"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Department
                </Button>
              </div>
              <DepartmentList 
                departments={departments} 
                loading={loading}
                onDelete={handleDeleteDepartment}
              />
            </Card>
          </TabsContent>

          <TabsContent value="kra-kpi" className="space-y-6">
            <KRAKPIManager 
              departments={departments} 
              selectedDept={selectedDept}
              setSelectedDept={setSelectedDept}
              tenantId={getActiveTenantId()}
            />
          </TabsContent>

          <TabsContent value="reviews" className="space-y-6">
            <ReviewManager 
              departments={departments}
              selectedDept={selectedDept}
              setSelectedDept={setSelectedDept}
              tenantId={getActiveTenantId()}
            />
          </TabsContent>

          <TabsContent value="analytics" className="space-y-6">
            <AnalyticsDashboard departments={departments} tenantId={getActiveTenantId()} />
          </TabsContent>

          <TabsContent value="profile" className="space-y-6">
            <ProfileSettings currentUsername={currentUsername} />
          </TabsContent>
        </Tabs>

        {/* Add Department Dialog */}
        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <DialogContent data-testid="add-dept-dialog">
            <DialogHeader>
              <DialogTitle>Add New Department</DialogTitle>
              <DialogDescription>
                Create a new department to manage KRAs, KPIs, and reviews.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label htmlFor="dept-name">Department Name *</Label>
                <Input
                  id="dept-name"
                  placeholder="e.g., Marketing"
                  value={newDeptName}
                  onChange={(e) => setNewDeptName(e.target.value)}
                  data-testid="dept-name-input"
                />
              </div>
              <div>
                <Label htmlFor="dept-head">Department Head Name</Label>
                <Input
                  id="dept-head"
                  placeholder="e.g., John Doe"
                  value={newDeptHead}
                  onChange={(e) => setNewDeptHead(e.target.value)}
                  data-testid="dept-head-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setShowAddDialog(false);
                  setNewDeptName('');
                  setNewDeptHead('');
                }}
                data-testid="cancel-dept-button"
              >
                Cancel
              </Button>
              <Button
                onClick={handleAddDepartment}
                disabled={submitting}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="submit-dept-button"
              >
                Add Department
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
          </>
        )}
      </div>
    </div>
  );
}